import { useMutation } from '@apollo/client/react';
import MediaControls from '@optools/components/media/media-controls';
import MediaPlayback from '@optools/components/media/media-playback';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import { GENERATE_PLAYBACK_URLS } from '@optools/services/operator-gateway/mutations';
import {
  Feed,
  MediaFeedType,
  MediaType,
  Playback,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { formatMonthDayYearHourMinuteSecond } from '@optools/utils/datetime';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { determineNotificationPosition } from '../utils/notification';

export interface VideoPanelProps {
  daiMode?: boolean;
  debugMode?: boolean;
  feed?: Feed;
  mediaId?: string;
  pipeline?: LoggerGamePipeline;
  side: MediaFeedType;
}

export function VideoPanel({
  daiMode = false,
  debugMode = false,
  feed,
  mediaId,
  pipeline,
  side,
}: VideoPanelProps) {
  const { postGameShow, preGameShow } = feed || { postGameShow: false, pregameShow: null };
  const { id } = pipeline || {};
  const isLive = ![
    StreamState.EVENT_ARCHIVE,
    StreamState.STREAM_DONE,
    StreamState.STREAM_OFF,
  ].includes(pipeline?.streamState ?? StreamState.EVENT_ARCHIVE);
  const [isStayLive, setIsStayLive] = useState(isLive);
  const [playbacks, setPlaybacks] = useState<Playback[]>([]);
  const [playbackUrls] = useMutation(GENERATE_PLAYBACK_URLS, {
    onCompleted: ({ generatePlaybackUrls }) => {
      const playbacks =
        generatePlaybackUrls?.[0]?.playbacks.sort((a, b) => {
          if (a.priority > b.priority) {
            return 1;
          }
          if (a.priority < b.priority) {
            return -1;
          }
          return 0;
        }) ?? [];
      const playbacksAvailable = playbacks.some((playback) => !!playback.url);
      setPlaybacks(playbacks);
      if (!playbacksAvailable) {
        toast.error('VideoPanel Error', {
          description: `No playback URLs available for ${side} feed.`,
          duration: Infinity,
          position: determineNotificationPosition(side),
        });
      }
    },
  });

  useEffect(() => {
    if (mediaId) {
      playbackUrls({
        variables: { mediaIds: [mediaId] },
      });
    }
  }, [mediaId, playbackUrls]);

  const handleOnChangeStayLive = (stayLive: boolean) => {
    setIsStayLive(stayLive);
  };

  const bestMatchingPlayback =
    playbacks?.find((playback) => playback.features?.includes('SDR')) ?? playbacks?.[0];

  return (
    <div className="flex w-full flex-col justify-center">
      {debugMode ? (
        <>
          <p className="rounded-sm border border-dashed p-1 text-xs">
            Pre-game: {`${preGameShow ? formatMonthDayYearHourMinuteSecond(preGameShow) : 'NO'}`}
          </p>
          <p className="rounded-sm border border-dashed p-1 text-xs">
            Post-game: {postGameShow ? 'YES' : 'NO'}
          </p>
          <p className="rounded-sm border border-dashed p-1 text-xs">Pipeline ID: {id}</p>
          <p className="rounded-sm border border-dashed p-1 text-xs">Media ID: {mediaId}</p>
        </>
      ) : null}
      <MediaPlayback
        autoPlay={true}
        controls={false}
        daiMode={daiMode}
        isStayLive={isStayLive}
        mediaType={MediaType.VIDEO}
        muted={true}
        playback={bestMatchingPlayback}
      />
      <MediaControls
        debugMode={debugMode}
        defaultStayLive
        live={isLive}
        onChangeStayLive={handleOnChangeStayLive}
      />
    </div>
  );
}
